import React, { useEffect, useMemo, useState } from "react";
import { Header } from "./component";
import { DataTable, MainLoader } from "CommonComponent";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { downloadInvoiceMonthZip, fetchInvoiceMonthSummary } from "actions";
import "./Invoices.css";

const Invoices = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const session = useSelector((state) => state.session);
  const invoicesState = useSelector((state) => state.invoicesState);
  const object_viewed_id = session?.current_page_permissions?.object_id || "";
  const [downloadingMonthKey, setDownloadingMonthKey] = useState("");

  useEffect(() => {
    dispatch(fetchInvoiceMonthSummary({}, object_viewed_id)).catch(() => {
      // Action handles snackbar/error state; avoid unhandled promise rejection.
    });
  }, [dispatch, object_viewed_id]);

  const rows = useMemo(() => {
    const source = invoicesState?.monthSummary;
    if (Array.isArray(source)) return source;
    if (Array.isArray(source?.items)) return source.items;
    if (Array.isArray(source?.data)) return source.data;
    if (Array.isArray(source?.data?.items)) return source.data.items;
    return [];
  }, [invoicesState?.monthSummary]);

  const resolveMonthKey = (row) => {
    const resolvedRow = row?.original || row;
    return (
      resolvedRow?.month_key ||
      resolvedRow?.monthKey ||
      resolvedRow?.month ||
      resolvedRow?.period ||
      ""
    );
  };

  const getMonthStatus = (row) => {
    const resolvedRow = row?.original || row || {};
    return String(
      resolvedRow?.status ||
      resolvedRow?.publish_status ||
      resolvedRow?.invoice_status ||
      "new"
    )
      .trim()
      .toLowerCase();
  };

  const getStatusTagMeta = (status) => {
    if (status === "unpublished") {
      return { label: "Unpublished", className: "bg-danger" };
    }

    if (status === "republished") {
      return { label: "Republished", className: "bg-info text-dark" };
    }

    if (["published", "complete", "completed"].includes(status)) {
      return { label: "Published", className: "bg-success" };
    }

    if (["processing", "publishing", "in_progress", "in-progress"].includes(status)) {
      return { label: "Publishing", className: "bg-warning text-dark" };
    }

    if (status === "failed") {
      return { label: "Failed", className: "bg-danger" };
    }

    return { label: "New", className: "bg-secondary" };
  };

  const isMonthReadonly = (row) => {
    const status = getMonthStatus(row);
    return [
      "published",
      "republished",
      "complete",
      "completed",
      "processing",
      "publishing",
      "in_progress",
      "in-progress",
    ].includes(status);
  };

  const getStatusCountValue = (row, fallbackKeys = []) => {
    const resolvedRow = row?.original || row || {};
    for (const key of fallbackKeys) {
      const raw = resolvedRow?.[key];
      if (raw !== undefined && raw !== null && raw !== "") {
        const parsed = Number(raw);
        return Number.isFinite(parsed) ? parsed : 0;
      }
    }

    return 0;
  };

  const handleEditClick = (row) => {
    const monthKey = resolveMonthKey(row);
    if (monthKey) {
      navigate(`/invoices/update?month_key=${encodeURIComponent(monthKey)}`);
      return;
    }

    navigate("/invoices/update");
  };

  const handleDownloadZip = (row) => {
    const monthKey = resolveMonthKey(row);
    if (!monthKey || downloadingMonthKey) return;

    setDownloadingMonthKey(monthKey);
    dispatch(downloadInvoiceMonthZip({ monthKey }, object_viewed_id))
      .catch(() => {
        // Action handles snackbar/error state.
      })
      .finally(() => {
        setDownloadingMonthKey("");
      });
  };

  const canDownloadMonthZip = (row) => {
    const resolvedRow = row?.original || row || {};
    if (resolvedRow?.can_download_all === true || resolvedRow?.can_download_all === 1 || resolvedRow?.can_download_all === "1" || resolvedRow?.can_download_all === "true") return true;
    if (resolvedRow?.is_publish_complete === true || resolvedRow?.is_publish_complete === 1 || resolvedRow?.is_publish_complete === "1" || resolvedRow?.is_publish_complete === "true") return true;

    const monthStatus = getMonthStatus(resolvedRow);
    if (["published", "republished", "completed", "complete"].includes(monthStatus)) return true;

    const published = Number(resolvedRow?.published_programs || 0);
    return published > 0;
  };

  const getActions = (row) => (
    <div className="d-flex align-items-center gap-2">
      <img
        src={isMonthReadonly(row) ? "/assets/images/view-icon.svg" : "/assets/images/edit-icon.svg"}
        alt={isMonthReadonly(row) ? "View" : "Edit"}
        style={{ cursor: "pointer" }}
        onClick={() => handleEditClick(row)}
      />
      <button
        type="button"
        className="border-0 bg-transparent p-0"
        style={{
          cursor: canDownloadMonthZip(row) && !downloadingMonthKey ? "pointer" : "not-allowed",
          opacity: canDownloadMonthZip(row) && !downloadingMonthKey ? 1 : 0.45,
        }}
        disabled={!canDownloadMonthZip(row) || Boolean(downloadingMonthKey)}
        onClick={() => handleDownloadZip(row)}
        title="Download published invoices ZIP"
      >
        <img src="/assets/images/download-icon.svg" alt="Download ZIP" width="16" height="16" />
      </button>
    </div>
  );

  const renderStatusTag = (row) => {
    const status = getMonthStatus(row);
    const tag = getStatusTagMeta(status);

    return (
      <span className={`badge ${tag.className}`}>
        {tag.label}
      </span>
    );
  };

  const columns = [
    {
      id: "month",
      header: "Month",
      accessor: (row) => row.month || row.month_label || row.monthKey || "-",
      sortable: true,
      searchable: true,
    },
    {
      id: "invoice_count",
      header: "# of Invoices",
      accessor: (row) => row.invoice_count ?? row.total_invoices ?? row.count ?? 0,
      sortable: true,
      searchable: true,
    },
    {
      id: "publish_count",
      header: "Publish Count",
      accessor: (row) =>
        getStatusCountValue(row, [
          "publish_count",
          "published_count",
          "published_programs",
        ]),
      sortable: true,
      searchable: true,
    },
    {
      id: "unpublish_count",
      header: "Unpublish Count",
      accessor: (row) =>
        getStatusCountValue(row, [
          "unpublish_count",
          "unpublished_count",
          "unpublished_programs",
        ]),
      sortable: true,
      searchable: true,
    },
    {
      id: "new_count",
      header: "New Count",
      accessor: (row) =>
        getStatusCountValue(row, ["new_count", "new_programs"]),
      sortable: true,
      searchable: true,
    },
    {
      id: "republish_count",
      header: "Republish Count",
      accessor: (row) =>
        getStatusCountValue(row, [
          "republish_count",
          "republished_count",
          "republished_programs",
        ]),
      sortable: true,
      searchable: true,
    },
    {
      id: "total_amount",
      header: "Total Amount",
      accessor: (row) => {
        const amount =
          Number(
            row.total_amount ?? row.total_invoice_amount ?? row.invoice_total ?? 0
          ) || 0;
        return `$${amount.toLocaleString()}`;
      },
      sortable: true,
      searchable: true,
    },
    {
      id: "status",
      header: "Status",
      accessor: null,
      cell: (_, row) => renderStatusTag(row),
      sortable: false,
      searchable: false,
    },
    {
      id: "actions",
      header: "Actions",
      accessor: null,
      cell: (_, row) => getActions(row),
      sortable: false,
      searchable: false,
    },
  ];

  return (
    <div className="wrapper-div">
      <MainLoader forceOpen={Boolean(downloadingMonthKey)} />
      <Header />
      <div>
        <DataTable
          columns={columns}
          data={rows}
          serverSide={false}
          showSearch={true}
          className="table-container"
        />
      </div>
    </div>
  );
};

export default Invoices;
